import { useEffect, useState } from 'react';
import VideoGenericElement from '$components/GenericElements/VideoGenericElement';
import { ReactComponent as CloseVideoModal } from '$icons/uncolored/Close.svg';
import { ReactComponent as Pencil } from '$icons/uncolored/pencil.svg';
import { CandidateData } from '$types/candidates';
import { useTranslation } from 'react-i18next';
import {
  useGetAllVideoFeedbacksQuery,
  useSendVideoCommentMutation,
} from '$store/modules/video-feedback';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  useGetRatingEmployerQuery,
  useUpdateRatingMutation,
} from '$store/modules/ratings';
import { segmentTrack } from '$utils/handleSegment';
import SPKToast from '$components/SPKToast/SPKToast';

import SPKEmployerCandidateVideosRate from './SPKEmployerCandidateVideosRate';

const SPKEmployerShowcaseVideoModal = ({
  selectedVideo,
  setSelectedVideo,
  candidate,
}: {
  selectedVideo: any;
  setSelectedVideo: (prop: any) => void;
  candidate: CandidateData;
}) => {
  const [t] = useTranslation('global');
  const [videoRating, setVideoRating] = useState(-1);

  const { data, isLoading } = useGetAllVideoFeedbacksQuery({
    videoId: selectedVideo.Id,
    videoType: 'AdditionalVideos',
  });

  const { data: employerRate } = useGetRatingEmployerQuery({
    data: {
      additionalVideoId: selectedVideo.Id,
    },
  });
  const { register, watch, setValue, handleSubmit } = useForm({
    defaultValues: {
      feedbackComment: '',
    },
  });

  const feedbackCommentValue = watch('feedbackComment');

  const [sendComment] = useSendVideoCommentMutation();

  const [updateRating] = useUpdateRatingMutation();

  const onSubmitFeedbackHandler: SubmitHandler<{ feedbackComment: string }> = ({
    feedbackComment,
  }) => {
    Promise.all([
      sendComment({
        videoId: selectedVideo.Id,
        videoType: 'AdditionalVideos',
        videoComment: feedbackComment,
      }),
      updateRating({
        data: {
          additionalVideoId: selectedVideo.Id,
          rate: videoRating + 1,
        },
        hideToast: true,
      }),
    ]).then(() => {
      segmentTrack('Video Rating', {
        video_id: selectedVideo.Id,
        video_type: 'AdditionalVideos',
        candidate_id: candidate.Id,
        rating: videoRating + 1,
      });
      SPKToast(t('VideoModal.FeedbackSent'));
      setSelectedVideo(undefined);
    });
  };

  useEffect(() => {
    if (data) setValue('feedbackComment', data?.[0]?.Comment || '');
  }, [data, setValue]);

  useEffect(() => {
    if (employerRate) setVideoRating(employerRate?.Rating || -1);
  }, [employerRate]);

  return (
    <div className="bg-white p-6 flex flex-col rounded-xl font-mulish md:min-w-[600px]">
      <button
        type="button"
        className="flex flex-row items-center gap-2 text-sm text-spk-mediumContrast ml-auto"
        onClick={() => setSelectedVideo(undefined)}
      >
        Close
        <CloseVideoModal className="stroke-spk-mediumContrast w-5 h-5" />
      </button>
      <div className="font-extrabold text-2xl text-spk-highContrast">
        {selectedVideo.Name}
      </div>
      <div className="text-sm text-spk-mediumContrast">{`${
        candidate?.User?.FirstName?.split(' ')[0] || ''
      } ${candidate?.User?.LastName?.split(' ')[0][0]}.`}</div>
      <div className="flex flex-col mt-8">
        <VideoGenericElement
          videoData={selectedVideo}
          style={{
            maxWidth: '80%',
            maxHeight: '25rem',
            alignSelf: 'center',
          }}
          controls
          playsInline
          className="rounded-xl"
        />
        <div className="flex flex-col justify-center items-center">
          <div className="mt-4 text-spk-mediumContrast text-sm">
            {t('VideoModal.RateCandidate')}
          </div>
          <SPKEmployerCandidateVideosRate
            additionalVideoId={selectedVideo.Id}
            videoRating={videoRating}
            setVideoRating={setVideoRating}
          />
        </div>
        <div className="mt-6 text-xs text-spk-mediumContrast">
          {t('VideoModal.FeedbackReason')}
        </div>
        <form onSubmit={handleSubmit(onSubmitFeedbackHandler)}>
          <fieldset disabled={isLoading}>
            <div className="mb-8 relative">
              <textarea
                className="outline-none min-h-24 w-full border border-spk-neutral-40 rounded-md flex justify-start overflow-hidden p-2 text-sm"
                {...register('feedbackComment')}
              />
              {feedbackCommentValue.length === 0 && (
                <Pencil className="fill-spk-mediumContrast absolute top-3 left-3" />
              )}
              <div className="text-xs text-spk-highContrast">
                {t('VideoModal.InternalUseOnly')}
              </div>
            </div>
            <button
              type="submit"
              className="bg-spk-brightBlue rounded-full px-4 py-2 text-spk-softBlue ml-auto flex disabled:opacity-25 disabled:cursor-not-allowed"
            >
              {t('VideoModal.SendFeedback')}
            </button>
          </fieldset>
        </form>
      </div>
    </div>
  );
};

export default SPKEmployerShowcaseVideoModal;
